import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { KeyRound, CloudDownload, LayoutDashboard, ArrowUpRight, Server, ListChecks, Radio, Wifi, FileSearch, Landmark, Scale, Camera } from "lucide-react";
import api from "@/lib/api";
import SiteNav from "@/components/SiteNav";
import TwitchLive from "@/components/TwitchLive";

/* Citizen Hub — the map of everything New Citizen. One page that answers:
   "what exists, where is it, and how do I use it?" for players AND server owners.
   In-game CEF surfaces are deliberately NOT linked here — they ship inside the game
   and open via direct URL only (the desktop launcher package uses them). */

const OWNER_STEPS = [
  { Icon: ListChecks, title: "1 · Register your listing", body: "Sign in, open Servers & Community, hit Register Your Server — name, region, game mode, done in 30 seconds." },
  { Icon: KeyRound, title: "2 · Prove it's yours", body: "Start a claim: put the NC-CLAIM token into your live server hostname, verify it, confirm with your 2FA code. Zero-trust — nobody can hijack your listing." },
  { Icon: CloudDownload, title: "3 · Install the bridge", body: "Claiming issues your server API key. Download nc_bridge.py, paste the key, run it next to your server — your world comes alive on the Hub." },
  { Icon: LayoutDashboard, title: "4 · Command your world", body: "The Owner Deck (/admin/hub) is your management menu: economy, taxes, ruleset, factions & ranks, theme, pairing wizard — changes push to your live server with no restart." },
];

export default function CitizenHubPage() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/nc/servers").catch(() => ({ data: { servers: [] } })),
      api.get("/nc/servers/steam").catch(() => ({ data: { servers: [] } })),
    ]).then(([h, s]) => {
      setStats({
        live: (h.data.servers || []).filter((x) => x.online).length + (s.data.servers || []).length,
        hub: (h.data.servers || []).length,
      });
    });
  }, []);

  return (
    <div data-testid="citizen-hub" className="min-h-screen bg-[#050507] text-zinc-100">
      <SiteNav />
      {/* hero */}
      <div className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(ellipse at 20% 0%, rgba(46,107,255,0.16) 0%, transparent 55%), radial-gradient(ellipse at 85% 100%, rgba(255,110,40,0.08) 0%, transparent 50%)" }} />
        <div className="relative max-w-6xl mx-auto px-6 py-16 sm:py-20">
          <img src="/assets/newcitizen-wordmark.png" alt="New Citizen" className="h-10 sm:h-14 object-contain mb-6" />
          <p className="font-mono text-[10px] tracking-[0.45em] uppercase text-nc-blue mb-3">Citizen Hub</p>
          <h1 className="font-bold uppercase tracking-wide text-2xl sm:text-4xl max-w-3xl leading-tight">Everything New Citizen lives here — play it, run it.</h1>
          <p className="font-mono text-xs text-zinc-400 mt-4 max-w-2xl leading-relaxed">
            One map for the whole ecosystem: browse live worlds, join RP communities,
            or start your own server and manage it end-to-end.
          </p>
          {stats && (
            <div className="flex gap-3 mt-6 flex-wrap">
              <span data-testid="hub-live-count" className="font-mono text-[9px] uppercase tracking-[0.2em] border border-emerald-500/40 text-emerald-400 px-3 py-2 flex items-center gap-1.5"><Wifi className="w-3 h-3" /> {stats.live} servers live now</span>
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] border border-white/15 text-zinc-400 px-3 py-2">{stats.hub} hub worlds registered</span>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12 flex flex-col gap-14">
        {/* PLAY */}
        <section data-testid="hub-play">
          <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-nc-blue mb-4">Play</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link data-testid="hub-open-servers" to="/new-citizen/servers"
              className="group border border-white/15 p-6 hover:border-nc-blue/60 transition-colors flex items-center gap-5">
              <Server className="w-8 h-8 text-zinc-300 shrink-0" strokeWidth={1.5} />
              <div className="flex-1">
                <p className="font-bold uppercase tracking-[0.15em] text-lg flex items-center gap-2">Servers & Community <ArrowUpRight className="w-4 h-4 text-zinc-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" /></p>
                <p className="font-mono text-xs text-zinc-300 mt-1 leading-relaxed">Browse live worlds on the web, register your own server, rent hosting, join RP communities.</p>
              </div>
            </Link>
            <Link data-testid="hub-open-casefiles" to="/new-citizen/case-files"
              className="group border border-amber-500/25 bg-amber-500/[0.03] p-6 hover:border-amber-500/50 transition-colors flex items-center gap-5">
              <FileSearch className="w-8 h-8 text-amber-400 shrink-0" strokeWidth={1.5} />
              <div className="flex-1">
                <p className="font-bold uppercase tracking-[0.15em] text-lg flex items-center gap-2">Case Files <ArrowUpRight className="w-4 h-4 text-amber-500/70 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" /></p>
                <p className="font-mono text-xs text-zinc-300 mt-1 leading-relaxed">The public record of Bornwood's closed mysteries — every culprit named, every detective credited.</p>
              </div>
            </Link>
            <Link data-testid="hub-open-museum" to="/new-citizen/museum"
              className="group border border-white/15 p-6 hover:border-nc-blue/60 transition-colors flex items-center gap-5">
              <Landmark className="w-8 h-8 text-zinc-300 shrink-0" strokeWidth={1.5} />
              <div className="flex-1">
                <p className="font-bold uppercase tracking-[0.15em] text-lg flex items-center gap-2">City Memory <ArrowUpRight className="w-4 h-4 text-zinc-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" /></p>
                <p className="font-mono text-xs text-zinc-300 mt-1 leading-relaxed">The museum of things that actually happened — cases, shocks and heists written into the timeline.</p>
              </div>
            </Link>
            <Link data-testid="hub-open-court" to="/new-citizen/court-records"
              className="group border border-white/15 p-6 hover:border-nc-blue/60 transition-colors flex items-center gap-5">
              <Scale className="w-8 h-8 text-zinc-300 shrink-0" strokeWidth={1.5} />
              <div className="flex-1">
                <p className="font-bold uppercase tracking-[0.15em] text-lg flex items-center gap-2">Court Records <ArrowUpRight className="w-4 h-4 text-zinc-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" /></p>
                <p className="font-mono text-xs text-zinc-300 mt-1 leading-relaxed">The public courtroom gallery — every verdict, fine and sentence, on display.</p>
              </div>
            </Link>
            <Link data-testid="hub-open-backdrop" to="/new-citizen/backdrop"
              className="group border border-nc-orange/25 bg-nc-orange/[0.03] p-6 hover:border-nc-orange/50 transition-colors flex items-center gap-5">
              <Camera className="w-8 h-8 text-nc-orange shrink-0" strokeWidth={1.5} />
              <div className="flex-1">
                <p className="font-bold uppercase tracking-[0.15em] text-lg flex items-center gap-2">Backdrop Contest <ArrowUpRight className="w-4 h-4 text-nc-orange/70 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" /></p>
                <p className="font-mono text-xs text-zinc-300 mt-1 leading-relaxed">Your best city shot becomes the main menu. Weekly crown, one vote per citizen.</p>
              </div>
            </Link>
          </div>
        </section>

        {/* OWN A SERVER */}
        <section data-testid="hub-own" className="border border-emerald-500/25 bg-emerald-500/[0.03] p-6 sm:p-8">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-emerald-400">Own a server</p>
            <span className="font-mono text-[8px] text-zinc-500 uppercase tracking-[0.2em]">from zero to your own city in about 15 minutes</span>
          </div>
          <h2 className="font-bold uppercase tracking-wide text-xl sm:text-2xl mb-6">How a player becomes a server owner</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {OWNER_STEPS.map(({ Icon, title, body }) => (
              <div key={title} className="border border-white/10 bg-black/40 p-5 flex flex-col gap-3">
                <Icon className="w-5 h-5 text-emerald-400" strokeWidth={1.5} />
                <p className="font-semibold uppercase tracking-[0.12em] text-sm">{title}</p>
                <p className="font-mono text-[11px] text-zinc-300 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
          <div className="flex gap-3 mt-6 flex-wrap">
            <Link data-testid="hub-register-cta" to="/new-citizen/servers" className="font-mono text-[10px] tracking-[0.25em] uppercase bg-emerald-500 text-black px-6 py-3.5 hover:bg-emerald-400 transition-colors flex items-center gap-2">
              <Server className="w-4 h-4" /> Register your server
            </Link>
            <Link data-testid="hub-deck-cta" to="/admin/hub" className="font-mono text-[10px] tracking-[0.25em] uppercase border border-white/20 text-zinc-200 px-6 py-3.5 hover:border-emerald-500/60 hover:text-emerald-400 transition-colors flex items-center gap-2">
              <LayoutDashboard className="w-4 h-4" /> Owner Deck (verified owners)
            </Link>
            <a data-testid="hub-bridge-cta" href="/bridge/nc_bridge.py" download className="font-mono text-[10px] tracking-[0.25em] uppercase border border-white/20 text-zinc-200 px-6 py-3.5 hover:border-nc-blue/60 hover:text-nc-blue transition-colors flex items-center gap-2">
              <CloudDownload className="w-4 h-4" /> nc_bridge.py
            </a>
          </div>
        </section>

        {/* how it connects */}
        <section data-testid="hub-how">
          <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-nc-blue mb-4">How it all connects</p>
          <div className="border border-white/10 bg-black/30 p-6 font-mono text-xs text-zinc-300 leading-relaxed flex flex-col gap-2">
            <p><span className="text-zinc-100">PLAYERS</span> — launch the game → main menu → pick a server → loading screen → they're in. Phone, CAD and courthouse open from inside the world.</p>
            <p><span className="text-zinc-100">STAFF</span> — the server bridge recognizes staff playercodes and mints admin sessions, which unlock the Admin Console overlay in-game.</p>
            <p><span className="text-zinc-100">OWNERS</span> — claim a listing, install the bridge, then run everything from the Owner Deck: economy, factions, ranks, theme, rules — no restarts.</p>
            <p className="text-zinc-600 flex items-center gap-1.5 mt-2"><Radio className="w-3 h-3" /> Every world is data-isolated per server — your citizens, cases and bank accounts never leak across servers.</p>
          </div>
        </section>
      </div>

      <TwitchLive compact />
    </div>
  );
}
